import { CATEGORY_TO_LABEL_MAP } from "constants/constants";
import { PnlCategoriesEnum } from "types/enums";
import { PnlLoss } from "types/types";
import { useFullRangeDataDump } from "utils/customHooks/genericHooks";
import { TopDowntime, PnlLossTagWithCategory } from "./topDowntimeChartUtils";
import { TopDowntimesChart } from "./TopDowntimesChart";

export function TopDowntimesChartConnected({
  numDowntimes,
}: {
  numDowntimes: number;
}) {
  const { topDowntimes, totalDurationMs } = useTopDowntimes(numDowntimes);
  return <TopDowntimesChart {...{ topDowntimes, totalDurationMs }} />;
}

function useTopDowntimes(num: number): {
  topDowntimes: TopDowntime[];
  totalDurationMs: number;
} {
  const fullRangeDDA = useFullRangeDataDump();

  const pnlLossTagsWithCategory = fullRangeDDA?.pnlLosses
    ? Object.entries(fullRangeDDA.pnlLosses).reduce((acc, [key, value]) => {
        // grab "tags" from all pnl categories
        if (value && typeof value === "object" && "tags" in value) {
          const tagsWithCategory = (value as PnlLoss).tags.map(t => ({
            ...t,
            pnlCategory: key as PnlCategoriesEnum,
          }));
          return [...acc, ...tagsWithCategory];
        }
        return acc;
      }, [] as PnlLossTagWithCategory[])
    : [];

  const downtimesSorted: TopDowntime[] = [
    ...pnlLossTagsWithCategory,
    // also show "unknown" category in addition to tags (since user will also want to reduce unknown time)
    {
      name: CATEGORY_TO_LABEL_MAP[PnlCategoriesEnum.unknown],
      downtimePlusDependentSpeedLossMs: fullRangeDDA?.unknownTime ?? 0,
      pnlCategory: PnlCategoriesEnum.unknown,
    },
  ]
    .map(tag => ({
      name: tag.name,
      durationMs: tag.downtimePlusDependentSpeedLossMs,
      pnlCategory: tag.pnlCategory,
    }))
    .sort((a, b) => b.durationMs - a.durationMs);

  const topDowntimes = downtimesSorted.slice(0, num);

  return {
    topDowntimes,
    totalDurationMs: downtimesSorted.reduce(
      (acc, downtime) => acc + downtime.durationMs,
      0
    ),
  };
}
